import * as React from "react";
import { Form, Formik } from "formik";
import { default as dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { Box, Button, Stack } from "@mui/material";
import { DATE_PICKER_FORMAT, DatePickerField } from "components/formikFields/DatePickerField/DatePickerField";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import { MtBillingType } from "gen/clients";
import { allMtBillingTypeOptions } from "pages/SystemSettingsPage/tabs/ApplcationsPage/utils/MtUtils";
import { SelectOption } from "@mui/base";

interface Props {
  from: Date;
  to: Date;
  billingType: MtBillingType | "";
  onChange: (from: Date, to: Date, billingType: MtBillingType | "") => void;
}

enum FieldNames {
  from = "from",
  to = "to",
  billingType = "billingType"
}

interface Values {
  [FieldNames.from]: string;
  [FieldNames.to]: string;
  [FieldNames.billingType]: MtBillingType | "";
}

const billingTypeOptions: SelectOption<string>[] = [{ label: "All", value: "" }, ...allMtBillingTypeOptions];

dayjs.extend(utc);
dayjs.extend(timezone);

const Filter: React.FC<Props> = ({ from, to, billingType, onChange }) => {
  const initialValue = React.useMemo(
    () => ({
      from: dayjs(from).format(DATE_PICKER_FORMAT),
      to: dayjs(to).format(DATE_PICKER_FORMAT),
      billingType
    }),
    [billingType, from, to]
  );

  const validate = React.useCallback((values: Values) => {
    const fromValue = dayjs(values[FieldNames.from], DATE_PICKER_FORMAT);
    const toValue = dayjs(values[FieldNames.to], DATE_PICKER_FORMAT);
    const errors: Record<string, string> = {};
    if (toValue.isBefore(fromValue)) {
      errors[FieldNames.to] = "Start date must be before end date";
    } else if (toValue.subtract(365, "day").isAfter(fromValue)) {
      errors[FieldNames.to] = "Search range cannot exceed 365 days";
    }
    return errors;
  }, []);

  const onSubmit = React.useCallback(
    (values: Values) => {
      window.console.log("onSubmit", { from: values[FieldNames.from], to: values[FieldNames.to] });
      const fromValue = dayjs
        .tz(values[FieldNames.from], DATE_PICKER_FORMAT, "America/Los_Angeles")
        .startOf("day")
        .toDate();
      const toValue = dayjs.tz(values[FieldNames.to], DATE_PICKER_FORMAT, "America/Los_Angeles").endOf("day").toDate();
      onChange(fromValue, toValue, values[FieldNames.billingType]);
    },
    [onChange]
  );

  return (
    <Formik initialValues={initialValue} validate={validate} onSubmit={onSubmit} enableReinitialize={true}>
      <Form autoComplete="off" autoCorrect="off">
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <DatePickerField
            name={FieldNames.from}
            label="Start date"
            variant="outlined"
            size="small"
            disableFuture={true}
          />
          <DatePickerField name={FieldNames.to} label="End date" variant="outlined" size="small" disableFuture={true} />
          <Box sx={{ minWidth: 220 }}>
            <SelectOneField
              name={FieldNames.billingType}
              label="Billing Type"
              options={billingTypeOptions}
              variant="outlined"
              size="small"
            />
          </Box>
          <Box>
            <Button type="submit" variant="contained">
              Search
            </Button>
          </Box>
        </Stack>
      </Form>
    </Formik>
  );
};

export { Filter };
